/**
 * Calendar-date helpers (#160 item 2, the timezone chain).
 *
 * Meal-plan dates are CALENDAR dates: `MealPlan.startDate` and
 * `MealSlot.date` are `@db.Date` columns, serialized by the API as
 * `"YYYY-MM-DDT00:00:00.000Z"` (UTC midnight of the calendar date). The
 * client works in the user's LOCAL calendar. Every timezone bug in this
 * area came from crossing that boundary with instant math:
 *
 * - `localDate.toISOString().split("T")[0]` renders the UTC date of a
 *   local instant -- one day ahead after 18:00 in UTC-negative zones, one
 *   day behind before local ~noon in UTC-positive zones.
 * - `new Date(slot.date).setHours(0,0,0,0)` floors the UTC-midnight
 *   instant to LOCAL midnight, shifting the calendar date back a day in
 *   every UTC-negative zone, all day long.
 *
 * The rule: convert both sides to calendar-date STRINGS with the two
 * helpers below and compare strings. Never compare instants across the
 * local/UTC boundary.
 */

/** Local Date -> its LOCAL calendar date as "YYYY-MM-DD". */
export function formatLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * The person's own calendar date, for writes whose outcome depends on which
 * week is "current". The API runs on UTC and cannot know: late on a Saturday
 * west of UTC its clock is already in next week. Sent as a header so it stays
 * out of validated, idempotency-hashed request bodies.
 */
export function clientLocalDateHeader(now: Date = new Date()): { "X-Client-Local-Date": string } {
  return { "X-Client-Local-Date": formatLocalDate(now) };
}

/** Local Sunday at midnight for the calendar week containing `date`. */
export function getStartOfWeek(date: Date): Date {
  const start = new Date(date);
  start.setDate(date.getDate() - date.getDay());
  start.setHours(0, 0, 0, 0);
  return start;
}

/**
 * The one name for "which week": the calendar date of that week's Sunday, as
 * "YYYY-MM-DD". A calendar date, never an instant, so it is timezone-free once
 * made: the weekday of a calendar date does not depend on where you read it,
 * and the API already stores weeks as the UTC midnight of this same string
 * (`canonicalWeekStart` in apps/api/src/services/meal-plan-week.ts).
 *
 * Only two functions produce one: `toWeekKey` (from the person's local clock)
 * and `parseWeekKey` (from text). The brand keeps an unchecked string, such as
 * a raw `?week=` value, from being passed where a week is expected.
 */
export type WeekKey = string & { readonly __brand: "WeekKey" };

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MILLIS_PER_DAY = 86_400_000;

/** The week a LOCAL date falls in. Local, because the person's own calendar decides. */
export function toWeekKey(date: Date): WeekKey {
  return formatLocalDate(getStartOfWeek(date)) as WeekKey;
}

/**
 * Text -> WeekKey, for input we do not control (`?week=`, `history.state`).
 * A real calendar date that is not a Sunday is normalised to its week rather
 * than rejected; anything else is `null`. Never throws.
 *
 * Pure UTC arithmetic on the date parts, so the answer is the same in every
 * zone. Takes a bare "YYYY-MM-DD" only: for a stored `@db.Date` value pass
 * `calendarDateOf(value)`.
 */
export function parseWeekKey(value: unknown): WeekKey | null {
  if (typeof value !== "string") return null;
  const match = CALENDAR_DATE.exec(value);
  if (!match) return null;

  const utcMidnight = new Date(`${value}T00:00:00.000Z`);
  // An impossible date either fails to parse or rolls over ("2026-02-30" ->
  // March); both are caught by requiring the round trip to be exact.
  if (Number.isNaN(utcMidnight.getTime())) return null;
  if (utcMidnight.toISOString().slice(0, 10) !== value) return null;

  const sunday = new Date(utcMidnight.getTime() - utcMidnight.getUTCDay() * MILLIS_PER_DAY);
  return sunday.toISOString().slice(0, 10) as WeekKey;
}

/** WeekKey -> that Sunday at LOCAL midnight, for display and local date math. */
export function weekKeyToLocalDate(weekKey: WeekKey): Date {
  const [, year, month, day] = CALENDAR_DATE.exec(weekKey)!;
  const local = new Date(2000, 0, 1);
  // setFullYear, not the Date constructor: the constructor reads years 0-99 as 19xx.
  local.setFullYear(Number(year), Number(month) - 1, Number(day));
  return local;
}

/**
 * Stored/API date string ("YYYY-MM-DDT00:00:00.000Z" or bare
 * "YYYY-MM-DD") -> its calendar date as "YYYY-MM-DD". For `@db.Date`
 * serializations the `T` split IS the calendar date -- do not route these
 * through `new Date(...)` and local accessors.
 */
export function calendarDateOf(isoDate: string): string {
  return isoDate.split("T")[0]!;
}
